import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  LogOut,
  TrendingUp,
  MousePointerClick,
  ShoppingBag,
  Wallet,
  Users,
  ArrowLeft,
  Zap,
  Copy,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCcw,
  Mail,
  Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  adminLogin,
  getAdminStats,
  getAdminClicks,
  getAdminPostbacks,
  getAdminOrders,
  getAdminLeads,
  getAdminPostbackUrls,
  adminSendDailySummary,
  getAdminEmailLog,
} from "@/lib/api";

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [isAuthed, setIsAuthed] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [tokenInput, setTokenInput] = useState("");

  // Verify saved token on mount
  useEffect(() => {
    if (token) verifyToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async (t) => {
    setAuthenticating(true);
    try {
      await adminLogin(t);
      localStorage.setItem("admin_token", t);
      setToken(t);
      setIsAuthed(true);
    } catch (e) {
      localStorage.removeItem("admin_token");
      setIsAuthed(false);
      if (e?.response?.status === 403) toast.error("Onjuiste admin token.");
    } finally {
      setAuthenticating(false);
    }
  };

  const onLoginSubmit = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return toast.error("Vul je admin token in.");
    await verifyToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setIsAuthed(false);
    setTokenInput("");
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700 mb-6">
            <ArrowLeft className="h-4 w-4" /> Naar de website
          </Link>
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)]">
            <div className="mx-auto h-12 w-12 rounded-xl bg-blue-700 text-white grid place-items-center mb-4">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 text-center">Admin login</h1>
            <p className="mt-1 text-sm text-slate-500 text-center">Voer je <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">ADMIN_TOKEN</code> uit <code>backend/.env</code> in</p>
            <form onSubmit={onLoginSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="admin-token" className="text-sm font-semibold text-slate-900 mb-1.5 block">Admin token</Label>
                <Input
                  id="admin-token"
                  type="password"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="Plak hier je geheime token"
                  autoFocus
                  data-testid="admin-token-input"
                />
              </div>
              <button
                type="submit"
                disabled={authenticating}
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg px-5 py-3 transition-all disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {authenticating ? <><Loader2 className="h-4 w-4 animate-spin" /> Inloggen...</> : "Inloggen"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return <DashboardContent onLogout={logout} />;
}

function DashboardContent({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [postbacks, setPostbacks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [postbackUrls, setPostbackUrls] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("overview");

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [s, c, p, o, l, u, em] = await Promise.all([
        getAdminStats(),
        getAdminClicks(),
        getAdminPostbacks(),
        getAdminOrders(),
        getAdminLeads(),
        getAdminPostbackUrls(),
        getAdminEmailLog(),
      ]);
      setStats(s);
      setClicks(c.clicks || []);
      setPostbacks(p.postbacks || []);
      setOrders(o.orders || []);
      setLeads(l.leads || []);
      setPostbackUrls(u);
      setEmailLog(em.logs || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Sessie verlopen — log opnieuw in.");
        onLogout();
        return;
      }
      toast.error(err?.response?.data?.detail || "Data laden mislukt.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerDailySummary = async () => {
    setSendingEmail(true);
    try {
      const r = await adminSendDailySummary();
      if (r.sent) {
        toast.success(`Dagrapport verzonden naar ${r.recipient}`);
        // reload email log
        const em = await getAdminEmailLog();
        setEmailLog(em.logs || []);
      } else {
        toast.error(r.error || "Verzenden mislukt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Verzenden mislukt");
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center">
                <Zap className="h-5 w-5" />
              </div>
              <span className="font-heading font-bold text-lg text-slate-900">EnergieKontract</span>
            </Link>
            <span className="hidden sm:inline-flex bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAll}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700 px-3 py-1.5 rounded-md hover:bg-slate-50 disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Vernieuw</span>
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-md hover:bg-slate-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log uit</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Conversie-dashboard</h1>
          <p className="text-slate-600 mt-1">Click → overstap funnel en omzet-overzicht</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard
            icon={MousePointerClick}
            color="blue"
            label="Vergelijker clicks"
            value={stats?.affiliate?.clicks_total ?? 0}
            sublabel={`7d: ${stats?.affiliate?.clicks_7d ?? 0} · 30d: ${stats?.affiliate?.clicks_30d ?? 0}`}
            testid="kpi-clicks"
          />
          <KpiCard
            icon={TrendingUp}
            color="emerald"
            label="Overstappen"
            value={stats?.affiliate?.conversions ?? 0}
            sublabel={`Conv: ${stats?.affiliate?.conversion_rate ?? 0}% · ${stats?.affiliate?.pending ?? 0} pending`}
            testid="kpi-conversions"
          />
          <KpiCard
            icon={Wallet}
            color="amber"
            label="Affiliate commissie"
            value={fmtEur(stats?.affiliate?.total_commission ?? 0)}
            sublabel="Cumulatief uitbetaald"
            testid="kpi-commission"
          />
          <KpiCard
            icon={ShoppingBag}
            color="slate"
            label="Stripe omzet"
            value={fmtEur(stats?.revenue?.total ?? 0)}
            sublabel={`${stats?.funnel?.paid_orders ?? 0} betaalde orders`}
            testid="kpi-revenue"
          />
        </div>

        {/* Funnel snapshot */}
        <FunnelStrip stats={stats} />

        {/* Tabs */}
        <div className="mt-8 mb-4 flex flex-wrap gap-2 border-b border-slate-200">
          {[
            ["overview", "Per vergelijker"],
            ["clicks", `Clicks (${clicks.length})`],
            ["postbacks", `Postbacks (${postbacks.length})`],
            ["orders", `Orders (${orders.length})`],
            ["leads", `Jurist-leads (${leads.length})`],
            ["email", `Email (${emailLog.length})`],
            ["setup", "Postback URLs"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === id
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <ProviderBreakdown stats={stats} />}
        {tab === "clicks" && <ClicksTable rows={clicks} />}
        {tab === "postbacks" && <PostbacksTable rows={postbacks} />}
        {tab === "orders" && <OrdersTable rows={orders} />}
        {tab === "leads" && <LeadsTable rows={leads} />}
        {tab === "email" && (
          <EmailPanel
            log={emailLog}
            sending={sendingEmail}
            onSend={triggerDailySummary}
          />
        )}
        {tab === "setup" && <PostbackSetup urls={postbackUrls} />}
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sublabel, testid }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <div className={`h-10 w-10 rounded-xl border grid place-items-center ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{sublabel}</div>
    </div>
  );
}

function FunnelStrip({ stats }) {
  if (!stats) return null;
  const f = stats.funnel || {};
  const items = [
    { label: "Analyses", value: f.analyses_total, hint: `${f.analyses_7d || 0} laatste 7 dagen` },
    { label: "Betaalde orders", value: f.paid_orders, hint: "Rapport + bundle" },
    { label: "Vergelijker clicks", value: f.comparison_clicks, hint: "Door naar partner" },
    { label: "Overstap conversies", value: f.conversions, hint: "Postback bevestigd" },
    { label: "Jurist-leads", value: f.lawyer_leads, hint: "Aanvragen ontvangen" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="funnel-strip">
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Funnel snapshot</h3>
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {items.map((it, i) => (
          <FunnelStep key={i} step={it} isLast={i === items.length - 1} />
        ))}
      </div>
    </div>
  );
}

function FunnelStep({ step, isLast }) {
  return (
    <>
      <div className="min-w-[120px] flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
        <div className="font-heading text-2xl font-bold text-slate-900">{step.value ?? 0}</div>
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mt-0.5">{step.label}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">{step.hint}</div>
      </div>
      {!isLast && <div className="text-slate-300 select-none px-1">→</div>}
    </>
  );
}

function ProviderBreakdown({ stats }) {
  const rows = stats?.affiliate?.by_provider || [];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="provider-breakdown">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-medium">Vergelijker</th>
            <th className="px-5 py-3 font-medium text-right">Clicks</th>
            <th className="px-5 py-3 font-medium text-right">Conversies</th>
            <th className="px-5 py-3 font-medium text-right">Conv. %</th>
            <th className="px-5 py-3 font-medium text-right">Commissie</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Nog geen clicks geregistreerd</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} data-testid={`provider-row-${r.provider_id}`}>
              <td className="px-5 py-3 font-medium text-slate-900">{r.provider_name}</td>
              <td className="px-5 py-3 text-right text-slate-700">{r.clicks}</td>
              <td className="px-5 py-3 text-right text-slate-700">{r.conversions}</td>
              <td className="px-5 py-3 text-right text-slate-700">{r.conversion_rate}%</td>
              <td className="px-5 py-3 text-right font-semibold text-emerald-700">{fmtEur(r.commission)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    declined: "bg-red-100 text-red-800 border-red-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    initiated: "bg-slate-100 text-slate-700 border-slate-200",
    failed: "bg-red-100 text-red-800 border-red-200",
    expired: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider border px-2 py-0.5 rounded-full ${cfg[status] || "bg-slate-100 text-slate-700"}`}>
      {status || "—"}
    </span>
  );
}

function ClicksTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="clicks-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-medium">Tijd</th>
            <th className="px-5 py-3 font-medium">Vergelijker</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium text-right">Commissie</th>
            <th className="px-5 py-3 font-medium">Wrapped?</th>
            <th className="px-5 py-3 font-medium">Click ID</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">Nog geen clicks</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-3 font-medium text-slate-900">{r.provider_id}</td>
              <td className="px-5 py-3"><StatusBadge status={r.conversion_status} /></td>
              <td className="px-5 py-3 text-right font-medium text-slate-900">{r.commission_amount ? fmtEur(r.commission_amount) : "—"}</td>
              <td className="px-5 py-3">{r.is_affiliate_wrapped ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}</td>
              <td className="px-5 py-3 text-xs text-slate-500 font-mono">{r.id?.slice(0, 8)}...</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PostbacksTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="postbacks-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-medium">Tijd</th>
            <th className="px-5 py-3 font-medium">Netwerk</th>
            <th className="px-5 py-3 font-medium">Click ID</th>
            <th className="px-5 py-3 font-medium">Match?</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium text-right">Commissie</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">Nog geen postbacks ontvangen — configureer eerst de postback-URL in je affiliate-netwerk</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-3 text-slate-600">{fmtDate(r.received_at)}</td>
              <td className="px-5 py-3 font-medium text-slate-900">{r.network}</td>
              <td className="px-5 py-3 text-xs text-slate-500 font-mono">{r.click_id ? `${r.click_id.slice(0, 8)}...` : "—"}</td>
              <td className="px-5 py-3">{r.matched ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}</td>
              <td className="px-5 py-3"><StatusBadge status={r.normalized_status} /></td>
              <td className="px-5 py-3 text-right font-medium text-slate-900">{r.commission ? fmtEur(r.commission) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="orders-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-medium">Tijd</th>
            <th className="px-5 py-3 font-medium">Pakket</th>
            <th className="px-5 py-3 font-medium">Klant</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium text-right">Bedrag</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Nog geen orders</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-3 font-medium text-slate-900">{r.package_id}</td>
              <td className="px-5 py-3 text-slate-700">{r.customer_email || "—"}</td>
              <td className="px-5 py-3"><StatusBadge status={r.payment_status} /></td>
              <td className="px-5 py-3 text-right font-semibold text-slate-900">{fmtEur(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadsTable({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="leads-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-medium">Tijd</th>
            <th className="px-5 py-3 font-medium">Naam</th>
            <th className="px-5 py-3 font-medium">Contact</th>
            <th className="px-5 py-3 font-medium">Urgentie</th>
            <th className="px-5 py-3 font-medium text-right">Geschat totaal</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Nog geen jurist-leads</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-3 font-medium text-slate-900">{r.name}</td>
              <td className="px-5 py-3 text-slate-700">{r.email} {r.phone ? `· ${r.phone}` : ""}</td>
              <td className="px-5 py-3"><StatusBadge status={r.urgency} /></td>
              <td className="px-5 py-3 text-right font-semibold text-slate-900">{fmtEur(r.estimated_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmailPanel({ log, sending, onSend }) {
  return (
    <div className="space-y-5" data-testid="email-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="email-trigger-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-700" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Dagelijkse admin-mail</h3>
            </div>
            <p className="mt-1.5 text-sm text-slate-600 max-w-xl">
              De backend stuurt elke ochtend om <strong>08:00 Nederlandse tijd</strong> een KPI-rapport.
              Hier kun je het rapport handmatig versturen om te testen.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Vereist: <code className="bg-slate-100 px-1.5 py-0.5 rounded">RESEND_API_KEY</code> en
              <code className="bg-slate-100 px-1.5 py-0.5 rounded ml-1">ADMIN_EMAIL</code> in backend/.env
            </p>
          </div>
          <button
            onClick={onSend}
            disabled={sending}
            className="inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg px-5 py-3 transition-all disabled:opacity-50"
            data-testid="email-send-now-btn"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Versturen...</>
            ) : (
              <><Send className="h-4 w-4" /> Verstuur nu</>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="email-log-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-medium">Tijd</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Ontvanger</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Onderwerp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {log.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Nog geen mails verzonden — klik "Verstuur nu" om te testen</td></tr>
            ) : log.map((r) => (
              <tr key={r.id} data-testid={`email-row-${r.id}`}>
                <td className="px-5 py-3 text-slate-600">{fmtDate(r.sent_at)}</td>
                <td className="px-5 py-3 font-medium text-slate-900">{r.type}</td>
                <td className="px-5 py-3 text-slate-700">{r.recipient}</td>
                <td className="px-5 py-3">
                  {r.ok ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> Verzonden
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700" title={r.error}>
                      <AlertCircle className="h-4 w-4" /> Mislukt
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-600 truncate max-w-xs">{r.subject}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostbackSetup({ urls }) {
  const copy = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`${label} URL gekopieerd`);
  };
  return (
    <div className="space-y-5" data-testid="postback-setup">
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
        <h3 className="font-heading font-semibold text-slate-900">Hoe te configureren</h3>
        <ol className="mt-3 text-sm text-slate-700 list-decimal list-inside space-y-1.5">
          <li>Log in op het paneel van het affiliate-netwerk (Daisycon / Awin / TradeTracker).</li>
          <li>Ga naar je publisher-instellingen → "Postback URL" of "Server-to-Server tracking".</li>
          <li>Plak de bijbehorende URL hieronder. De <code className="bg-white px-1 rounded">{`{ws}`}</code> / <code className="bg-white px-1 rounded">{`{ti}`}</code> / etc. zijn macro's die het netwerk automatisch invult.</li>
          <li>Klaar — bij elke conversie wordt het dashboard automatisch bijgewerkt.</li>
        </ol>
      </div>

      {urls && (
        <>
          <PostbackUrlBlock
            title="Daisycon postback URL"
            description="Pricewise + Gaslicht.com. Zet deze URL bij Daisycon onder 'Postback URL' in je publisher-instellingen."
            url={urls.daisycon}
            onCopy={(u) => copy(u, "Daisycon")}
            testid="postback-url-daisycon"
          />
          <PostbackUrlBlock
            title="Awin postback URL"
            description="Independer. Configureer onder 'Master Tag Manager / Postback URL'."
            url={urls.awin}
            onCopy={(u) => copy(u, "Awin")}
            testid="postback-url-awin"
          />
          <PostbackUrlBlock
            title="TradeTracker postback URL"
            description="Energievergelijk.nl. Configureer onder 'Conversion Tracking → Server-Side'."
            url={urls.tradetracker}
            onCopy={(u) => copy(u, "TradeTracker")}
            testid="postback-url-tradetracker"
          />
        </>
      )}
    </div>
  );
}

function PostbackUrlBlock({ title, description, url, onCopy, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-heading font-semibold text-slate-900">{title}</h4>
          <p className="text-sm text-slate-500 mt-1">{description}</p>
        </div>
        <button
          onClick={() => onCopy(url)}
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md"
          data-testid={`${testid}-copy`}
        >
          <Copy className="h-3.5 w-3.5" /> Kopieer
        </button>
      </div>
      <pre className="mt-3 bg-slate-900 text-emerald-300 text-[11px] sm:text-xs p-3 rounded-lg overflow-x-auto">
        {url}
      </pre>
    </div>
  );
}
